import { Play } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import type { AnimationEvent, AnimationPlayer, PitchSnapshot } from "@/types"

import { MiniReplayCard } from "./mini-replay-card"

interface KeyPlayHeroImageProps {
  title: string
  imageUrl?: string
  replaySnapshot: PitchSnapshot | null
  replayEvents: AnimationEvent[] | null
  playersData: Record<string, AnimationPlayer> | null
  homeTeamId: string
  homeTeamHue: number
  awayTeamHue: number
  opacity: number
  scale: number
  translateY: number
  rotate: number
  blur: number
  glowIntensity: number
}

export function KeyPlayHeroImage({
  title,
  imageUrl,
  replaySnapshot,
  replayEvents,
  playersData,
  homeTeamId,
  homeTeamHue,
  awayTeamHue,
  opacity,
  scale,
  translateY,
  rotate,
  blur,
  glowIntensity,
}: KeyPlayHeroImageProps) {
  const [flipped, setFlipped] = useState(false)
  const [hasFlippedOnce, setHasFlippedOnce] = useState(false)
  const [paused, setPaused] = useState(false)
  const [replayKey, setReplayKey] = useState(0)

  const hasReplay = !!(replaySnapshot && replayEvents && playersData)

  const handleFlip = () => {
    if (!hasReplay) return
    setFlipped((prev) => !prev)
    if (!hasFlippedOnce) setHasFlippedOnce(true)
  }

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation()
    setPaused((p) => !p)
  }

  const handleReplay = (e: React.MouseEvent) => {
    e.stopPropagation()
    setReplayKey((k) => k + 1)
    setPaused(false)
  }

  return (
    <div
      className="h-full w-full"
      style={{
        opacity,
        transform: `translateY(${translateY}px) scale(${scale}) rotate(${rotate}deg)`,
        filter: `blur(${blur}px)`,
        willChange: "opacity, transform, filter",
        perspective: "2000px",
      }}
    >
      {/* Glow burst */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          opacity: glowIntensity * 0.4,
          background:
            "radial-gradient(ellipse at center, oklch(0.78 0.18 55 / 0.2) 0%, transparent 60%)",
        }}
      />

      <div
        className="relative h-full w-full cursor-pointer"
        role="button"
        tabIndex={0}
        style={{
          transformStyle: "preserve-3d",
          transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
        onClick={handleFlip}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleFlip()
          }
        }}
      >
        {/* Front face — AI image or atmospheric placeholder */}
        <div
          className="absolute inset-0"
          style={{ backfaceVisibility: "hidden" }}
        >
          {imageUrl ? (
            <div className="relative h-full w-full">
              <img
                src={imageUrl}
                alt={title}
                className="h-full w-full object-cover"
              />
              {/* Scanlines overlay */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(0deg, transparent, transparent 2px, oklch(0 0 0 / 0.06) 2px, oklch(0 0 0 / 0.06) 4px)",
                }}
              />
              {hasReplay && (
                <span
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 border px-4 py-1.5 text-[11px] font-bold tracking-wider uppercase"
                  style={{
                    color: "oklch(0.55 0.08 55)",
                    borderColor: "oklch(0.30 0.06 55)",
                    background: "oklch(0.15 0.03 55 / 0.8)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  Tap to see the play
                </span>
              )}
            </div>
          ) : (
            <div
              className="flex h-full w-full flex-col items-center justify-center"
              style={{
                background:
                  "radial-gradient(ellipse at center, oklch(0.18 0.04 55), oklch(0.10 0.02 55))",
              }}
            >
              {/* Scanlines */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(0deg, transparent, transparent 2px, oklch(0 0 0 / 0.06) 2px, oklch(0 0 0 / 0.06) 4px)",
                }}
              />
              {/* Center line */}
              <div
                className="absolute inset-0 opacity-[0.05]"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, transparent 49.5%, oklch(0.9 0.05 80) 49.5%, oklch(0.9 0.05 80) 50.5%, transparent 50.5%)",
                }}
              />
              <svg
                className="mb-3 size-16"
                style={{
                  color: `oklch(${0.35 + glowIntensity * 0.25} 0.10 55)`,
                  filter: `drop-shadow(0 0 12px oklch(0.78 0.18 55 / ${glowIntensity * 0.4}))`,
                }}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="2" width="20" height="20" rx="2" />
                <circle cx="12" cy="12" r="4" />
                <path d="M2 12h4M18 12h4M12 2v4M12 18v4" />
              </svg>
              <span
                className="font-display text-lg font-black tracking-wider uppercase md:text-xl"
                style={{
                  color: `oklch(${0.35 + glowIntensity * 0.2} 0.08 55)`,
                  textShadow: `0 0 20px oklch(0.78 0.18 55 / ${glowIntensity * 0.3})`,
                }}
              >
                {title}
              </span>
              {hasReplay && (
                <span
                  className="mt-4 border px-4 py-1.5 text-[11px] font-bold tracking-wider uppercase"
                  style={{
                    color: "oklch(0.55 0.08 55)",
                    borderColor: "oklch(0.30 0.06 55)",
                    background: "oklch(0.15 0.03 55 / 0.8)",
                  }}
                >
                  Tap to see the play
                </span>
              )}
              {!hasReplay && (
                <span
                  className="mt-4 text-[11px] tracking-wider uppercase"
                  style={{ color: "oklch(0.30 0.03 55)" }}
                >
                  Replay unavailable
                </span>
              )}
            </div>
          )}
        </div>

        {/* Back face — pitch fills viewport */}
        <div
          className="absolute inset-0"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {hasFlippedOnce && hasReplay ? (
            <MiniReplayCard
              key={replayKey}
              replaySnapshot={replaySnapshot}
              replayEvents={replayEvents}
              playersData={playersData}
              homeTeamId={homeTeamId}
              homeTeamHue={homeTeamHue}
              awayTeamHue={awayTeamHue}
              paused={paused}
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center"
              style={{ background: "oklch(0.12 0.02 55)" }}
            >
              <span className="text-sm text-[var(--muted-foreground)]">
                Loading...
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Playback controls — bottom center, only when flipped */}
      {flipped && hasReplay && (
        /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
        <div
          className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            type="button"
            variant="ghost"
            onClick={handleReplay}
            className="flex h-9 w-9 items-center justify-center transition-colors hover:brightness-125"
            style={{
              background: "oklch(0.18 0.03 55 / 0.85)",
              backdropFilter: "blur(8px)",
              color: "oklch(0.65 0.08 55)",
              boxShadow: "0 2px 12px oklch(0 0 0 / 0.5)",
            }}
            title="Replay"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-4"
            >
              <path d="M3 12a9 9 0 1 1 9 9" />
              <polyline points="1 17 3 21 7 19" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={handlePlayPause}
            className="flex h-11 w-11 items-center justify-center transition-colors hover:brightness-125"
            style={{
              background: "oklch(0.25 0.05 55 / 0.85)",
              backdropFilter: "blur(8px)",
              color: "var(--accent)",
              boxShadow:
                "0 0 14px oklch(0.78 0.18 55 / 0.2), 0 2px 12px oklch(0 0 0 / 0.5)",
            }}
            title={paused ? "Play" : "Pause"}
          >
            {paused ? (
              <Play className="size-5" fill="currentColor" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="size-5"
              >
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={handleFlip}
            className="flex h-9 w-9 items-center justify-center transition-colors hover:brightness-125"
            style={{
              background: "oklch(0.18 0.03 55 / 0.85)",
              backdropFilter: "blur(8px)",
              color: "oklch(0.65 0.08 55)",
              boxShadow: "0 2px 12px oklch(0 0 0 / 0.5)",
            }}
            title="Back to card"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-4"
            >
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </Button>
        </div>
      )}
    </div>
  )
}
